import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function CustomOrderCTA() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.cta-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('cta-visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(t('customcta.whatsapp'));
    window.open(`https://wa.me/34631611077?text=${msg}`, '_blank');
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-20 sm:py-28 overflow-hidden bg-[#060406]"
    >
      {/* Background layers */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(155,48,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(155,48,255,0.6) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        {/* Central radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-purple-900/20 blur-[90px]" />
        {/* Side accent glows */}
        <div className="absolute top-0 left-0 w-[350px] h-[350px] rounded-full bg-purple-950/30 blur-[80px]" />
        <div className="absolute bottom-0 right-0 w-[350px] h-[350px] rounded-full bg-purple-950/30 blur-[80px]" />
      </div>

      {/* Top border line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />
      {/* Bottom border line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">

        {/* Label badge */}
        <div className="cta-reveal inline-flex items-center gap-2 border border-purple-500/30 bg-purple-900/10 px-4 py-2 rounded-full mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse block" />
          <span className="text-purple-400 text-xs tracking-widest uppercase font-medium">
            {t('customcta.label')}
          </span>
        </div>

        {/* Main heading */}
        <h2
          className="cta-reveal font-display text-3xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight"
          style={{ letterSpacing: '0.03em' }}
        >
          {t('customcta.title')}
        </h2>

        {/* Separator line with icon */}
        <div className="cta-reveal flex items-center justify-center gap-4 mb-6">
          <div className="flex-1 max-w-[120px] h-px bg-gradient-to-r from-transparent to-purple-500/50" />
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-search-eye-line text-purple-400 text-xl" />
          </div>
          <div className="flex-1 max-w-[120px] h-px bg-gradient-to-l from-transparent to-purple-500/50" />
        </div>

        {/* Subtitle */}
        <p className="cta-reveal font-body text-white/55 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
          {t('customcta.subtitle')}
        </p>

        {/* Feature pills */}
        <div className="cta-reveal flex flex-wrap justify-center gap-3 mb-12">
          {[
            { icon: 'ri-shield-check-line', key: 'customcta.feat1' },
            { icon: 'ri-global-line', key: 'customcta.feat2' },
            { icon: 'ri-time-line', key: 'customcta.feat3' },
            { icon: 'ri-vip-crown-line', key: 'customcta.feat4' },
          ].map((feat) => (
            <div
              key={feat.key}
              className="flex items-center gap-2 border border-purple-500/20 bg-purple-900/10 px-4 py-2 rounded-full"
            >
              <i className={`${feat.icon} text-purple-400 text-sm`} />
              <span className="text-white/65 text-xs font-medium tracking-wide whitespace-nowrap">
                {t(feat.key)}
              </span>
            </div>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="cta-reveal flex flex-col sm:flex-row items-center justify-center gap-4">
          {/* WhatsApp primary button */}
          <button
            onClick={handleWhatsApp}
            className="group relative flex items-center gap-3 gradient-purple text-white font-semibold tracking-wider uppercase text-sm px-8 py-4 rounded-full cursor-pointer whitespace-nowrap overflow-hidden transition-all duration-300 hover:scale-105"
            style={{
              boxShadow:
                '0 0 24px rgba(155, 48, 255, 0.45), 0 0 48px rgba(155, 48, 255, 0.2)',
            }}
          >
            {/* Shimmer effect */}
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.12) 50%, transparent 70%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s ease infinite',
              }}
            />
            <span className="w-5 h-5 flex items-center justify-center relative z-10">
              <i className="ri-whatsapp-line text-lg" />
            </span>
            <span className="relative z-10">{t('customcta.btn.whatsapp')}</span>
          </button>

          {/* Secondary scroll-to-contact */}
          <button
            onClick={() => {
              const el = document.querySelector('#contacto');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex items-center gap-2 border border-purple-500/35 text-white/75 hover:text-white hover:border-purple-400/60 text-sm font-medium tracking-wider uppercase px-8 py-4 rounded-full cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-purple-900/15"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-send-line text-purple-400" />
            </span>
            {t('customcta.btn.contact')}
          </button>
        </div>


      </div>

      <style>{`
        .cta-reveal {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.75s ease, transform 0.75s ease;
        }
        .cta-reveal.cta-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}